'use client';

import React, { useEffect, useState, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

const SERVICE_LABELS: Record<string, string> = {
  'chassis-alignment': 'Chassis Alignment',
  'airbag-srs': 'Airbag & SRS Repair',
  'module-coding': 'Module Coding',
  'body-repairs': 'Body Repairs',
};

const FAQ_ITEMS = [
  {
    q: 'How do I book a service?',
    a: "Simply fill out the booking form on any service page. You\'ll receive a confirmation email with your booking reference within minutes.",
  },
  {
    q: 'What happens after I book?',
    a: "We\'ll call you within 1 hour during business hours to confirm your appointment time and answer any questions.",
  },
  {
    q: 'Do I need to pay anything when I book?',
    a: "No. For non-fault claims, you pay nothing. For private repairs, we'll discuss payment when we call to confirm your appointment.",
  },
  {
    q: 'Can I change or cancel my booking?',
    a: "Yes, just call us on 0161 400 1687 and we'll be happy to help.",
  },
  {
    q: 'What if I need to send photos?',
    a: 'For Car-O-Liner and Body Repairs, you can upload photos directly in the booking form. For other services, email photos to contactus@nolimitauto.co.uk with your booking reference.',
  },
  {
    q: 'How long will my repair take?',
    a: "It depends on the extent of damage. We\'ll give you a clear timeline when we call to confirm your appointment.",
  },
  {
    q: 'Do you provide courtesy cars?',
    a: 'Yes, for non-fault claims and many private repairs. Ask when we call to confirm your booking.',
  },
  {
    q: 'I need urgent help – what should I do?',
    a: 'Call us immediately on 0161 400 1687. Do not use the online booking form for emergencies.',
  },
];

function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="w-full max-w-[680px] mt-16 mb-8">
      <h2
        className="font-fraunces text-2xl md:text-3xl font-bold text-white mb-8 text-center"
      >
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {FAQ_ITEMS.map((item, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="rounded-lg overflow-hidden"
              style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <button
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none"
                aria-expanded={isOpen}
              >
                <span className="font-dm-sans text-[15px] font-semibold text-white pr-4 leading-snug">
                  {item.q}
                </span>
                <span
                  className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-lg transition-transform duration-300"
                  style={{
                    background: '#CC1A1A',
                    color: '#fff',
                    transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                  }}
                  aria-hidden="true"
                >
                  +
                </span>
              </button>
              <div
                style={{
                  maxHeight: isOpen ? '300px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease',
                }}
              >
                <p className="font-dm-sans text-[14px] leading-relaxed px-5 pb-5 pt-0" style={{ color: '#C0C0C0' }}>
                  {item.a}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-8 text-center">
        <Link
          href="/contact"
          className="font-dm-sans text-sm font-semibold transition-colors duration-200"
          style={{ color: '#CC1A1A' }}
        >
          Still have questions? Contact us →
        </Link>
      </div>
    </section>
  );
}

function BookingConfirmedContent() {
  const searchParams = useSearchParams();
  const service = searchParams.get('service') || '';
  const ref = searchParams.get('ref') || '';
  const serviceLabel = SERVICE_LABELS[service] || service;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col items-center px-6 py-16"
      style={{ background: '#121212' }}
    >
      <div
        className="w-full max-w-[600px] flex flex-col items-center text-center"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        {/* Animated green checkmark */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'scale(1)' : 'scale(0.4)',
            transition: 'opacity 0.5s cubic-bezier(0.34,1.56,0.64,1), transform 0.5s cubic-bezier(0.34,1.56,0.64,1)',
            transitionDelay: '0.1s',
            marginBottom: '2rem',
          }}
        >
          <svg
            width="80"
            height="80"
            viewBox="0 0 80 80"
            fill="none"
            aria-label="Success checkmark"
          >
            <circle cx="40" cy="40" r="38" fill="#16a34a" opacity="0.15" />
            <circle cx="40" cy="40" r="32" fill="#16a34a" opacity="0.25" />
            <circle cx="40" cy="40" r="26" fill="#16a34a" />
            <path
              d="M26 40.5L35.5 50L54 31"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Headline */}
        <h1
          className="font-fraunces text-3xl md:text-4xl font-bold text-white mb-3 leading-tight"
          style={{ color: '#ffffff' }}
        >
          Booking Request Received!
        </h1>

        {/* Service name if provided */}
        {serviceLabel && (
          <p
            className="font-dm-sans text-sm font-semibold uppercase tracking-widest mb-4"
            style={{ color: '#CC1A1A' }}
          >
            {serviceLabel}
          </p>
        )}

        {/* Subheading */}
        <p className="font-dm-sans text-lg text-white/80 mb-4 leading-relaxed">
          Thank you for choosing{' '}
          <span className="font-semibold text-white">NO LIMIT AUTO BODY SPECIALIST</span>.
        </p>

        {/* Body text */}
        <p className="font-dm-sans text-[15px] text-white/60 leading-relaxed mb-6 max-w-[480px]">
          We&apos;ve received your booking request and will call you within{' '}
          <span className="text-white font-semibold">1 hour</span> during business hours
          (Mon–Fri 8am–6pm, Sat 8am–4pm). A confirmation email is on its way to you.
        </p>

        {/* Booking Reference */}
        {ref && (
          <div
            className="w-full rounded-lg p-5 mb-6 text-center"
            style={{
              background: '#1a1a1a',
              border: '1px solid rgba(204,26,26,0.35)',
              borderLeft: '4px solid #CC1A1A',
            }}
          >
            <p className="font-dm-sans text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Your Booking Reference
            </p>
            <p className="font-dm-sans text-2xl font-bold tracking-widest" style={{ color: '#CC1A1A' }}>
              {ref}
            </p>
            <p className="font-dm-sans text-xs mt-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Save this reference to track your booking at{' '}
              <Link href="/track-booking" className="underline" style={{ color: 'rgba(255,255,255,0.55)' }}>
                nolimitauto.co.uk/track-booking
              </Link>
            </p>
          </div>
        )}

        {/* Urgent contact info box */}
        <div
          className="w-full rounded-lg p-5 mb-8 text-left"
          style={{
            background: '#1a1a1a',
            borderLeft: '4px solid #CC1A1A',
            border: '1px solid rgba(204,26,26,0.25)',
            borderLeftWidth: '4px',
          }}
        >
          <p className="font-dm-sans text-sm font-semibold text-white mb-1">
            Need to speak to us urgently?
          </p>
          <a
            href="tel:01614001687"
            className="font-dm-sans text-base font-bold transition-colors"
            style={{ color: '#CC1A1A' }}
          >
            0161 400 1687
          </a>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Link
            href="/"
            className="flex items-center justify-center gap-2 px-6 py-3.5 rounded font-dm-sans text-sm font-bold uppercase tracking-widest transition-all duration-200"
            style={{
              background: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#ffffff',
            }}
          >
            ← Back to Home
          </Link>
          <Link
            href="/services/chassis-alignment"
            className="flex items-center justify-center gap-2 px-6 py-3.5 rounded font-dm-sans text-sm font-bold uppercase tracking-widest transition-all duration-200"
            style={{ background: '#CC1A1A', color: '#ffffff' }}
          >
            Our Services →
          </Link>
        </div>
      </div>

      {/* FAQ Accordion */}
      <FAQAccordion />
    </div>
  );
}

export default function BookingConfirmedPage() {
  return (
    <Suspense
      fallback={
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ background: '#121212' }}
        >
          <div className="w-8 h-8 rounded-full border-2 border-[#CC1A1A] border-t-transparent animate-spin" />
        </div>
      }
    >
      <BookingConfirmedContent />
    </Suspense>
  );
}
